import { FINANCE_PRODUCTS_UK } from '../../Constants/Filters/PriceFilter.constants';
import type { LocationAttrs } from '../../Entities/Location/LocationAttr.entity';
import type { AggregationsState } from '../../Entities/Search/Aggregations/Aggregations.entity';
import type { FilterValueDisplayObj } from '../../Entities/Search/SearchFilters/FilterValueDisplayObj.entity';
import { getFormattedCurrency } from '../../Utils/Intl/GetFormattedCurrency';
import { ukLocale } from '../../Utils/Intl/MappedLocales';
import { slugPartToWords } from '../../Utils/Search/SlugPartToWords';
import { capitalCase } from '../../Utils/Strings/CapitalCase';

type UKFinanceProducts = 'pcp' | 'hp';

export type FilterStrings = {
  filtersDoorsUnit: string;
  filtersEmissionUnit: string;
  minBhp: string;
  maxBhp: string;
  bhp: string;
  kw: string;
  filtersEngineSizeUnit: string;
  filtersEvRangeUnit: string;
  filtersEvRangeMinLabel: string;
  filtersAny: string;
  within: string;
  minMileage: string;
  maxMileage: string;
  filtersDistanceUnit: string;
  minPrice: string;
  'stock-condition-new': string;
  'stock-condition-used': string;
  maxPrice: string;
  filtersSeatsUnit: string;
  otherColors: string;
  onlyOnHeyCar: string;
  heyConExclusive: string;
  hideSold: string;
};

const getEngineSizeStr = (value: string | undefined, strings: Record<string, string>) => {
  return value ? `${value}${strings.filtersEngineSizeUnit}` : strings.filtersAny;
};

export const getFilterValueDisplayName = (
  filter: FilterValueDisplayObj,
  strings: Record<string, string>,
  aggregations: Pick<AggregationsState, 'makes' | 'models' | 'variants'>,
  locationAttrs?: LocationAttrs,
) => {
  const value = filter.value!;
  const { id, valueGte, valueLte } = filter;
  const locationAttrsObj = locationAttrs || { regions: undefined, areas: undefined };
  const { areas } = locationAttrsObj;
  const isClosedRange = valueGte === valueLte;
  const valOrAny = (value: string | undefined) => value || strings.filtersAny;

  // Some values need custom formatting
  if (id === 'doors') return `${value} ${strings.filtersDoorsUnit}`;

  if (id === 'emission-sticker') return `${strings.filtersEmissionUnit} ${value}`;

  if (id === 'engine-power')
    return isClosedRange
      ? `${valueGte || strings.minBhp} ${strings.bhp}`
      : `${valueGte || strings.minBhp} - ${valueLte || strings.maxBhp} ${strings.bhp}`;

  if (id === 'engine-size')
    return isClosedRange
      ? getEngineSizeStr(valueGte, strings)
      : `${getEngineSizeStr(valueGte, strings)} - ${getEngineSizeStr(valueLte, strings)}`;

  if (id === 'max-distance') return `${strings.within} ${value} ${strings.filtersDistanceUnit}`;

  if (id === 'ev-wltp-range')
    return `${valOrAny(valueGte)} ${strings.filtersEvRangeUnit} ${strings.filtersEvRangeMinLabel}`;

  if (id === 'mileage') {
    return `${
      valueGte ? Intl.NumberFormat(ukLocale).format(Number(valueGte)) : strings.minMileage
    } - ${
      valueLte ? Intl.NumberFormat(ukLocale).format(Number(valueLte)) : strings.maxMileage
    } ${strings.filtersDistanceUnit}`;
  }

  if (id === 'stock-condition') return strings[`stock-condition-${value}`];

  if (id === 'price' || id === 'monthly-price' || id === 'cascade-price')
    return `${valueGte ? getFormattedCurrency(Number(valueGte)) : strings.minPrice} - ${
      valueLte ? getFormattedCurrency(Number(valueLte)) : strings.maxPrice
    }`;

  if (id === 'postcode') return `${filter.postcode!}`;

  if (id === 'area') {
    return `${areas?.find(area => filter?.value === area?.id)?.shortName}`;
  }

  if (id === 'seats') return `${value} ${strings.filtersSeatsUnit}`;

  if (value === 'others' && id === 'color') {
    return strings.otherColors;
  }

  if (id === 'make') {
    const makeData = aggregations.makes.filter(make => make.key === value);
    return makeData[0] ? makeData[0].displayName : slugPartToWords(value);
  }

  if (id === 'model') {
    const modelData = aggregations.models.filter(model => model.key === value);
    return modelData[0] ? modelData[0].displayName : slugPartToWords(value);
  }

  if (id === 'variant-type') {
    const variantData = aggregations.variants.filter(variant => variant.key === value);
    const variantIdParts = value.split('-');
    return variantData[0] ? variantData[0].displayName : slugPartToWords(variantIdParts[2] ?? '');
  }

  if (
    id === 'finance-product' &&
    Object.prototype.hasOwnProperty.call(FINANCE_PRODUCTS_UK, value)
  ) {
    return FINANCE_PRODUCTS_UK[value as UKFinanceProducts].label;
  }

  if (id === 'eligible-products') {
    return strings.onlyOnHeyCar;
  }

  if (id === 'vehicle-status') return strings.hideSold;
  if (id === 'fuel-consumption') return `${valueGte || '0'} - ${valueLte || '20'} l/100km`;

  //Purchase Pro specific
  if (id === 'ending-soon') return strings.endingSoon;
  if (id === 'auction-grade') return `${strings.grade} ${value}`;
  if (id === 'mot') {
    if (value === 'expiring-soon') {
      return strings.motExpiring;
    }
    return strings.motNotValid;
  }
  if (id === 'sold-as-seen') return strings.soldAsSeen;
  if (id === 'v5') return strings.v5;
  if (id === 'previous-owners') return `${value} ${strings.previousOwners}`;
  if (id === 'cap-database') return strings[value] || capitalCase(value);

  // Generic formatting
  if (value === 'true') return strings[id];

  if (valueGte || valueLte) {
    return isClosedRange ? valOrAny(valueGte) : `${valOrAny(valueGte)} - ${valOrAny(valueLte)}`;
  }

  const intlValue = strings[value];

  return capitalCase(intlValue || value || '');
};
